import React, { createContext, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/browser'
import { useSelector } from 'react-redux'

// Actions
import { execute } from '../../../vendor/recaptcha'

// Providers
import { useWizardProvider } from '../../../Components/shared/WizardContext'

// Utils
import { createNewBuyerLead } from '../../../api/mikasa/token_requests'
import { chromeBotSignInLink } from '../../../api/mikasa/chromebot/customerRequests'

// Helpers
import { setAgentToken } from '../../../auth/widget'
import { requestClose } from '../../../api/iframe/requests'
import { isIFramed } from '../../../helpers/browser'
import { dispatchEvent } from '../../../helpers/tagManager'
import {
  SignUpAccountCreated,
  SignUpEmailEntered,
} from '../../../constants/trackingEvents'

// Constants
import { SIGN_UP_STEPS } from '../../../constants/signUp'

// Selectors
import {
  selectCustomerId,
  selectCustomerPublicKey,
} from '../../../store/selectors/customerProfile'

const SignUpContext = createContext({
  customerPublicKey: '',
  customerProfileId: '',
  redirectUrl: '',
})

const SignUpProvider = ({ children }) => {
  const [loading, setLoading] = useState(false)
  const [formState, setFormState] = useState({
    email: '',
    firstName: '',
    lastName: '',
  })

  const [formErrors, setFormErrors] = useState({})
  const [inputError, setInputError] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  // Props from iframe
  const [redirectUrl, setRedirectUrl] = useState('')
  const [customerPublicKey, setCustomerPublicKey] = useState('')
  const [customerProfileId, setCustomerProfileId] = useState('')
  const iFramed = isIFramed()

  // Customer Profile id and public key when not iframed
  const customerProfileIdFromState = useSelector(selectCustomerId)
  const customerProfilePublicKeyFromState = useSelector(selectCustomerPublicKey)

  // Wizard
  const { goForward, goTo } = useWizardProvider()

  const handleIframeClose = () => {
    if (iFramed) {
      requestClose()
    }
  }

  const validateForm = event => {
    const { name } = event.target
    const isValid = event.target.validity.valid

    if (isValid) {
      setFormErrors(prevState => ({
        ...prevState,
        [name]: false,
      }))
    } else {
      setFormErrors(prevState => ({
        ...prevState,
        [name]: true,
      }))
    }
  }

  const handleOnChange = event => {
    const { name, value } = event.target
    validateForm(event)

    setFormState(prevState => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleCheckClient = async () => {
    if (formErrors.email) {
      setInputError(true)
      return
    }

    setLoading(true)

    try {
      await chromeBotSignInLink(redirectUrl, formState.email)
      dispatchEvent(SignUpEmailEntered)
      goTo(SIGN_UP_STEPS.COMPLETE)
    } catch (err) {
      switch (err.response.status) {
        case 404:
          goForward()
          break
        case 403:
          goTo(SIGN_UP_STEPS.CLIENT_NOT_FOUND)
          break
        default:
          console.error(err)
          Sentry.captureException(err)
      }
    }

    setLoading(false)
    setInputError(false)
  }

  const handleResetClick = () => {
    goTo(SIGN_UP_STEPS.USER_EMAIL)
  }

  const handleCreateClick = async () => {
    setLoading(true)

    let validationToken
    try {
      validationToken = await execute({ action: 'property_search' })
    } catch (e) {
      setSubmitError(true)
      setLoading(false)
      console.error(e)
      Sentry.captureException(e)

      return
    }

    const { email, firstName, lastName } = formState
    try {
      await createNewBuyerLead({
        email,
        firstName,
        lastName,
        redirectUrl,
        validationToken,
      })
      dispatchEvent(SignUpAccountCreated)
      goForward()
    } catch (err) {
      setSubmitError(true)
      setLoading(false)
      console.error(err)
      Sentry.captureException(err)
    }
  }

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search)
    const params = Object.fromEntries(urlSearchParams.entries())

    if (params.redirectUrl) {
      setRedirectUrl(params.redirectUrl)
    }

    const cpId = params.customerProfileId || customerProfileIdFromState
    const cpPublicKey =
      params.customerPublicKey || customerProfilePublicKeyFromState

    setCustomerProfileId(cpId)
    setCustomerPublicKey(cpPublicKey)
    setAgentToken(cpPublicKey)
  }, [iFramed, customerProfileIdFromState, customerProfilePublicKeyFromState])

  const store = {
    iFramed,
    customerProfileId,
    customerPublicKey,
    formErrors,
    formState,
    handleIframeClose,
    handleOnChange,
    handleCheckClient,
    handleCreateClick,
    handleResetClick,
    inputError,
    submitError,
    loading,
    redirectUrl,
    setFormState,
    setInputError,
    setLoading,
    validateForm,
    setSubmitError,
  }

  return (
    <SignUpContext.Provider value={store}>{children}</SignUpContext.Provider>
  )
}

const useSignUpProvider = () => {
  const context = useContext(SignUpContext)

  if (!context) {
    throw new Error('useSignUpProvider must be used within a SignUpProvider')
  }

  return context
}

SignUpProvider.propTypes = {
  children: PropTypes.any,
}

export { SignUpProvider, useSignUpProvider, SignUpContext }
